import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import orgmsLogo from '../images/ggsf_logo.jpeg';
import { Button, Image } from 'react-bootstrap';
import { DepositContext } from '../context/DepositContext';
import useAuth from '../context/useAuth';

const DepositTicketView = () => {
    const { id } = useParams(); // Get the ID from the URL
    const [ticketDetails, setTicketDetails] = useState(null);
    const [checks, setChecks] = useState([]);
    const [checksTotal, setChecksTotal] = useState(0.0); // Default to a number
    const [orgDetails, setOrgDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const { depositData, setDepositData } = useContext(DepositContext);
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const pagetkt = queryParams.get('pagetkt');
    const { role } = useAuth();

    useEffect(() => {
        const fetchTicketDetails = async () => {
            try {
                // Fetch the deposit ticket details
                const response = await api.get(`/api/depositTickets/${id}`);
                setTicketDetails(response.data);

                // Fetch checks and checks total
                const { start_range, end_range } = response.data;
                const checksResponse = await api.get('/api/checkreceipts', {
                    params: { startDate: start_range, endDate: end_range },
                });

                setChecks(checksResponse.data.data || []);
                // Ensure checksTotal is a number
                setChecksTotal(parseFloat(checksResponse.data.totalDonation) || 0.0);

                // Fetch organization details
                const orgResponse = await api.get('/api/appconfig');
                setOrgDetails(orgResponse.data.data);

            } catch (error) {
                setErrorMessage(
                    error.response?.data?.error || 'Failed to fetch deposit ticket details.'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchTicketDetails();
    }, [id]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (errorMessage) {
        return <div className="alert alert-danger">{errorMessage}</div>;
    }

    if (!ticketDetails) {
        return <div>No details available for this deposit ticket.</div>;
    }

    const { deptkt_date, start_range, end_range, ...otherDetails } = ticketDetails;

    // Filter keys starting with 'deno_'
    const denominations = Object.entries(otherDetails).filter(([key]) =>
        key.startsWith('deno_')
    );

    // Calculate Currency Total
    const currencyTotal = denominations.reduce((sum, [key, count]) => {
        const denominationValue = parseInt(key.replace('deno_', ''), 10);
        const subtotal = count * denominationValue;
        return sum + subtotal;
    }, 0);

    // Ensure checksTotal is used as a number
    const grandTotal = currencyTotal + (typeof checksTotal === 'number' ? checksTotal : 0);

    const handlePrintAll = () => {
        window.print();
    };

    const handleEdit = (id) => {
        // Navigate to the edit deposit ticket screen
        history.push(`/editDepositTicket/${id}`);
    };

    const handleDelete = async (id) => {
        // Show a confirmation dialog
        const confirm = window.confirm(`Are you sure you want to delete this Deposit Ticket with ID: ${id}?`);
       
        if (!confirm) return;

        try {
            // Call the API to delete the item
            const response = await api.delete(`/api/depositTickets/${id}`);

            if (response.status === 200 || response.status === 204) {
                // Navigate to the deposit ticket list screen upon success
                alert(` Deposit Ticket ID: ${id} deleted successfully.`);
                history.push(`/depositTicketsList`);
            } else {
                throw new Error(`Failed to delete. Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error deleting item:', error);
            alert('Failed to delete the item. Please try again.');
        }
    };

    const handleGoBack = () => {
        if (pagetkt) {
            // Navigate back to the add new deposit ticket
            history.push(`/addNewDepositTicket`);
        }
        else {
            if (depositData) {
                // Update context with the current depositData
                setDepositData({
                    currentPage: depositData.currentPage,
                    startRange: depositData.startRange,
                    endRange: depositData.endRange,
                });
                // Navigate back to the deposit tickets list
                history.push(`/depositTicketsList`);
            } else {
                // Default go back behavior
                history.goBack();
            }
        }
    };

 
      const formatDateMMDDYYYY = (dateStr) => {
        // Check if dateStr is undefined, null, or an empty string
        if (!dateStr) {
            return ''; // Return an empty string or any default value if dateStr is not valid
        }

        // Split the date string into its components
        const [year, month, day] = dateStr.split('-');

        // Return the formatted date as MM/DD/YYYY
        return `${month}/${day}/${year}`;
    };
    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-end no-print">
                {(role === 'ops' || role === 'admin') && (
                    <Button
                        className="no-print small-font"
                        style={{ marginRight: '8px' }}
                        onClick={() => handleEdit(id)}
                    >
                        Edit
                    </Button>
                )}
                {(role === 'admin') && (
                    <Button
                        className="no-print small-font"
                        style={{ marginRight: '8px' }}
                        onClick={() => handleDelete(id)}
                    >
                        Delete
                    </Button>
                )}
                <Button className="no-print small-font" style={{ marginRight: '8px' }} onClick={handlePrintAll}>Print</Button>
                <Button className="btn btn-secondary no-print small-font" onClick={handleGoBack}>
                    Go Back
                </Button>
            </div>
            {/* Org Summary */}
            {!errorMessage && orgDetails && (
                <div className="summary-section mb-4">
                    <div className="summary-left">
                        <p className="bold-text small-font">{orgDetails.name}</p>
                        <p className="small-font">{orgDetails.address} {orgDetails.address2}</p>
                        <p className="small-font">{orgDetails.city}, {orgDetails.state} {orgDetails.zip}</p>
                        <p className="small-font">Phone: {orgDetails.phone}</p>
                    </div>
                    <div className="summary-center">
                        <div className="image-container">
                            <Image
                                src={orgmsLogo}
                                alt="ORG Logo"
                                fluid
                                style={{
                                    width: '200px',
                                    height: 'auto'
                                }}
                            />
                        </div>
                    </div>
                    <div className="summary-right">
                        <p className="bold-text small-font">Deposit Ticket ID: {id}</p>
                        <p className="bold-text small-font">
                            Deposit Ticket Date: {formatDateMMDDYYYY(deptkt_date)}
                        </p>
                        <p className="bold-text small-font">
                            Range: {formatDateMMDDYYYY(start_range)} TO {formatDateMMDDYYYY(end_range)}
                        </p>
                        <p className="bold-text small-font">
                            Currency Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(currencyTotal || 0)}
                        </p>
                        <p className="bold-text small-font">
                            Checks Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(checksTotal || 0)}
                        </p>
                        <p className="bold-text small-font">
                            Grand Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(grandTotal || 0)}
                        </p>
                    </div>
                </div>

            )}

            {/* Deposit Ticket Details */}
            <div className="card shadow">
                <div className="card-header bg-secondary text-white">
                    <h3 className="mb-0">Deposit Ticket Details</h3>
                </div>
                <div className="card-body">
                    <h5>Account No: __________________</h5>
                    {/* Denominations Section */}
                    <h4 className="mt-4 mb-3">Currency Denominations</h4>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Denomination</th>
                                <th>Count</th>
                                <th>Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {denominations.map(([key, count], index) => {
                                const denominationValue = parseInt(key.replace('deno_', ''), 10);
                                const subtotal = count * denominationValue;
                                return (
                                    <tr key={index}>
                                        <td>{denominationValue} x </td>
                                        <td>{count}</td>
                                        <td>{subtotal.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {/* Currency Total */}
                    <div className="row total-row mt-4">
                        <div className="col-label">
                            <h5 className="mb-0">Currency Total:</h5>
                        </div>
                        <div className="col-value">
                            <input
                                type="text"
                                className="form-control font-weight-bold text-end"
                                readOnly
                                value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(currencyTotal || 0)}
                                style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                            />
                        </div>
                    </div>
                    {/* Checks Section */}
                    <h4 className="mt-4 mb-3">Checks</h4>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Check Number</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checks.length > 0 ? (
                                checks.map((check, index) => (
                                    <tr key={index}>
                                        <td>{check.paymentReference}</td>
                                        <td>{check.donationAmt}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" className="text-center">No checks available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {/* Checks Total */}
                    <div className="row total-row mt-4">
                        <div className="col-label">
                            <h5 className="mb-0">Checks Total:</h5>
                        </div>
                        <div className="col-value">
                            <input
                                type="text"
                                className="form-control font-weight-bold text-end"
                                readOnly
                                value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(checksTotal || 0)}
                                style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                            />
                        </div>
                    </div>
                    {/* Grand Total */}
                    <div className="row total-row mt-4">
                        <div className="col-label">
                            <h4 className="mb-0">Grand Total:</h4>
                        </div>
                        <div className="col-value">
                            <input
                                type="text"
                                className="form-control font-weight-bold text-end"
                                readOnly
                                value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(grandTotal || 0)}
                                style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                            />
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DepositTicketView;